import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiURL } from '../../../../../utilities/URLs'
import { account } from '../../../../../classes/Account'
import { Tile } from '../../classes/Tile'
import { refreshAccessToken } from '../../../../../utilities/extensions'
import { AccelerateApplication } from '../../classes/AcclerateApplication'

function renderFormattedText(text) {
  return text.split('\\n').map((line, index) => {
    const parts = []
    let remaining = line
    let match
    const boldRegex = /<b>(.*?)<\/b>/

    while ((match = boldRegex.exec(remaining))) {
      const [fullMatch, boldText] = match
      const before = remaining.slice(0, match.index)
      if (before) parts.push(before)
      parts.push(<strong className='text-xl' key={parts.length}>{boldText}</strong>)
      remaining = remaining.slice(match.index + fullMatch.length)
    }

    if (remaining) parts.push(remaining)

    return <p className={`${index !== 0 ? 'mt-4' : ''}`} key={index}>{parts}</p>
  })
}

export default function DashboardStepTile({ applicationId }) {
  const navigate = useNavigate()
  const [tile, setTile] = useState(new Tile())
  const [accelerateApplication, setAccelerateApplication] = useState(new AccelerateApplication())
  const [submittingDecision, setSubmittingDecision] = useState(false)
  const [submittedDecision, setSubmittedDecision] = useState(false)

  useEffect(() => {
    const handleGetTile = async () => {
      const tileResponse = await fetch(`${apiURL}/api/applications/${applicationId}/dashboard/tile/`, {
        method: 'GET',
        headers: {
          'Authorization': account.accessToken
        }
      })
      const tileData = await tileResponse.json()
      if (tileData.response.error) {
        if (tileData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleGetTile() : navigate('/', { state: { expired: true } })
        } else
          console.error(tileData.response.error)
      } else
        setTile(new Tile(tileData.response.dashboardTile))
    }

    const handleGetAccelerateApplication = async () => {
      const accelerateApplicationResponse = await fetch(`${apiURL}/api/applications/${applicationId}/accelerate/`, {
        method: 'GET',
        headers: {
          'Authorization': account.accessToken
        }
      })
      const accelerateApplicationData = await accelerateApplicationResponse.json()
      if (accelerateApplicationData.response.error) {
        if (accelerateApplicationData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleGetAccelerateApplication() : navigate('/', { state: { expired: true } })
        } else
          console.error(accelerateApplicationData.response.error)
      } else
        setAccelerateApplication(new AccelerateApplication(accelerateApplicationData.response.accelerateApplication))
    }

    handleGetTile()
    handleGetAccelerateApplication()
  }, [applicationId, navigate])

  const handleNavigate = () => {
    let path = tile.navigate
    if (path.startsWith('https')) {
      window.location.href = path
      return
    }

    if (path.includes('{applicationId}'))
      path = path.replace('{applicationId}', applicationId)

    navigate(path)
  }

  /**
   * @param {Boolean} isAccelerate 
   */
  const handleAccelerateProgram = async (isAccelerate) => {
    setSubmittingDecision(true)

    const accelerateResponse = await fetch(`${apiURL}/api/applications/${applicationId}/accelerate/`, {
      method: 'POST',
      headers: {
        'Authorization': account.accessToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        isAccelerate
      })
    })
    const accelerateData = await accelerateResponse.json()
    if (accelerateData.response.error) {
      if (accelerateData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleAccelerateProgram(isAccelerate) : navigate('/', { state: { expired: true } })
      } else
        console.error(accelerateData.response.error)
    } else {
      console.log(accelerateData.response)
      if (accelerateData.response.stageId === '963baa43-dd34-4a76-969d-a73abe2df905')
        navigate(`/applications/${applicationId}/questionnaire`)
      else
        setSubmittedDecision(true)
    }

    setSubmittingDecision(false)
  }

  return (
    <div className='w-full lg:w-1/3 p-6 flex-1 rounded-2xl bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 shadow-sm flex flex-col items-center gap-4 transition-colors duration-300'>
      {tile.stageId ? (
        <div className='w-full h-full flex flex-col'>
          <p className='text-start w-full text-slate-800 dark:text-white text-lg sm:text-xl font-bold mb-4'>{tile.title.split(':')[0]}<span className='text-red-500 dark:text-red-400'>{tile.title.split(':')[1] ? `: ${tile.title.split(':')[1]}` : ''}</span></p>
          <div className='text-slate-600 dark:text-slate-300 text-sm xl:text-base leading-relaxed mb-4 flex-1'>{renderFormattedText(tile.description)}</div>
          {tile.stageId === 'ce12bfb5-8604-4ff5-bc46-75ff8fb5d02f' || tile.stageId === 'c3dda7e7-0f14-4a71-af4f-719076cbb121' ? (
            ((accelerateApplication.stage === 1 && accelerateApplication.registrationLink) ? (
              <button onClick={() => window.location.href = accelerateApplication.registrationLink} className='w-full sm:w-3/4 py-3 rounded-xl bg-ertc-primary text-white mt-auto hover:bg-ertc-primary/90 transition-transform active:scale-95 self-center font-bold'>
                Register Now
              </button>
            ) : (
              <button onClick={() => window.location.href = 'https://accelerated.ertcrefund.com'} className='w-full sm:w-3/4 py-3 rounded-xl bg-ertc-primary text-white mt-auto hover:bg-ertc-primary/90 transition-transform active:scale-95 self-center font-bold'>
                Login Now
              </button>
            ))
          ) : tile.navigate === 'yesno' ? (
            <div className='flex flex-row items-center gap-4 mt-auto'>
              {submittedDecision ? (
                <p className='w-full text-center text-sm text-slate-500 dark:text-slate-400'>Please check your email for the next steps. The message will come from accelerated@ertcrefund.com. If you don't see it right away, be sure to check your spam or junk folder.</p>
              ) : (
                <div className='w-full flex flex-col items-center gap-4'>
                  <div className='w-full flex flex-row items-center gap-3'>
                    <button disabled={submittingDecision} onClick={() => handleAccelerateProgram(true)} className='flex-1 py-3 rounded-xl bg-ertc-primary text-white self-center font-bold transition-transform active:scale-95 disabled:bg-slate-400 disabled:cursor-not-allowed'>
                      Yes
                    </button>
                    <button disabled={submittingDecision} onClick={() => handleAccelerateProgram(false)} className='flex-1 py-3 rounded-xl bg-red-600 dark:bg-red-500 text-white self-center font-bold transition-transform active:scale-95 disabled:bg-slate-400 disabled:cursor-not-allowed'>
                      No
                    </button>
                  </div>
                  {submittingDecision && (
                    <p>Submitting...</p>
                  )}
                </div>
              )}
            </div>
          ) : (
            tile.buttonText && (
              <button onClick={handleNavigate} className='w-full sm:w-3/4 py-3 rounded-xl bg-ertc-primary text-white mt-auto hover:bg-ertc-primary/90 transition-transform active:scale-95 self-center font-bold'>
                {tile.buttonText}
              </button>
            )
          )}
        </div>
      ) : (
        <p className='italic text-slate-400 dark:text-slate-500 self-center m-auto'>No Current Step Required At This Time.</p>
      )}
    </div>
  )
}